import React, { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { FaSmile, FaTrophy, FaHeadset, FaUsers } from "react-icons/fa";

const Stats = () => {
  const [counts, setCounts] = useState({
    projects: 0,
    hackathons: 0,
    hours: 0,
    collaborations: 0,
  });

  const stats = useMemo(
    () => [
      {
        icon: FaSmile,
        end: 10,
        title: "Projects",
        subtitle: "delivered across Web3 and MERN",
        color: "text-blue-500",
      },
      {
        icon: FaTrophy,
        end: 4,
        title: "Hackathons/Events",
        subtitle: "active participation",
        color: "text-green-500",
      },
      {
        icon: FaHeadset,
        end: 1000,
        title: "Hours Of Learning",
        subtitle: "continuous upskilling",
        color: "text-purple-500",
      },
      {
        icon: FaUsers,
        end: 5,
        title: "Collaborations",
        subtitle: "team-driven builds",
        color: "text-orange-500",
      },
    ],
    []
  );

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Start counting animation
            stats.forEach((stat, index) => {
              setTimeout(() => {
                animateCount(stat.end, stat.title.toLowerCase());
              }, index * 200);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.5 }
    );

    const element = document.getElementById("stats");
    if (element) {
      observer.observe(element);
    }

    return () => observer.disconnect();
  }, [stats]);

  const animateCount = (end, key) => {
    const duration = 2000;
    const start = 0;
    const increment = end / (duration / 16);
    let current = start;

    const timer = setInterval(() => {
      current += increment;
      if (current >= end) {
        current = end;
        clearInterval(timer);
      }

      setCounts((prev) => ({
        ...prev,
        [key]: Math.floor(current),
      }));
    }, 16);
  };

  return (
    <section id="stats" className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((stat, index) => (
            <motion.div
              key={stat.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="text-center group"
            >
              <motion.div
                whileHover={{ scale: 1.05, y: -5 }}
                className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all duration-300"
              >
                <div
                  className={`w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center group-hover:bg-blue-50 transition-colors duration-300`}
                >
                  <stat.icon
                    className={`text-3xl ${stat.color} group-hover:scale-110 transition-transform duration-300`}
                  />
                </div>

                <motion.div
                  className="text-4xl font-bold text-gray-900 mb-2"
                  key={counts[stat.title.toLowerCase()]}
                >
                  {counts[stat.title.toLowerCase()]}+
                </motion.div>

                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {stat.title}
                </h3>

                <p className="text-gray-600 text-sm">{stat.subtitle}</p>
              </motion.div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Stats;
